// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { buildEvent } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { CURRENCIES } from '../../lib/currencies.js';
import type {
  AccountType,
  AccountCreatedPayload,
  AccountUpdatedPayload,
  AccountDeletedPayload,
  Account,
} from '../../types.js';

type AccountTypeLabelKey =
  | 'bank' | 'cash' | 'assetPlaceholder' | 'mutualFund' | 'stock' | 'accountsReceivable'
  | 'creditCard' | 'liabilityPlaceholder' | 'accountsPayable' | 'income' | 'expense'
  | 'equity' | 'trading';
type AccountGroupKey = 'assets' | 'liabilities' | 'income' | 'expenses' | 'equity';

const ACCOUNT_TYPES: { value: AccountType; labelKey: AccountTypeLabelKey; groupKey: AccountGroupKey }[] = [
  { value: 'BANK', labelKey: 'bank', groupKey: 'assets' },
  { value: 'CASH', labelKey: 'cash', groupKey: 'assets' },
  { value: 'ASSET', labelKey: 'assetPlaceholder', groupKey: 'assets' },
  { value: 'MUTUAL_FUND', labelKey: 'mutualFund', groupKey: 'assets' },
  { value: 'STOCK', labelKey: 'stock', groupKey: 'assets' },
  { value: 'ACCOUNTS_RECEIVABLE', labelKey: 'accountsReceivable', groupKey: 'assets' },
  { value: 'CREDIT_CARD', labelKey: 'creditCard', groupKey: 'liabilities' },
  { value: 'LIABILITY', labelKey: 'liabilityPlaceholder', groupKey: 'liabilities' },
  { value: 'ACCOUNTS_PAYABLE', labelKey: 'accountsPayable', groupKey: 'liabilities' },
  { value: 'INCOME', labelKey: 'income', groupKey: 'income' },
  { value: 'EXPENSE', labelKey: 'expense', groupKey: 'expenses' },
  { value: 'EQUITY', labelKey: 'equity', groupKey: 'equity' },
  { value: 'TRADING', labelKey: 'trading', groupKey: 'assets' },
];

const OPENING_BALANCE_TYPES = new Set<AccountType>([
  'BANK', 'CASH', 'ASSET', 'CREDIT_CARD', 'LIABILITY',
  'ACCOUNTS_RECEIVABLE', 'ACCOUNTS_PAYABLE',
]);

const COMMODITY_TYPES = new Set<AccountType>(['STOCK', 'MUTUAL_FUND']);

interface AccountFormProps {
  editAccount?: Account;
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  onClose: () => void;
}

const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle';
const labelCls = 'block text-sm text-text-muted mb-1';

export function AccountForm({ editAccount, worker, keyData, onClose }: AccountFormProps) {
  const { t } = useTranslation(['accounting', 'transactions', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const accounts = [...ledger.accounts.values()];
  const isEdit = !!editAccount;
  const homeCurrency = ledger.settings.homeCurrency;

  const [name, setName] = useState(editAccount?.name ?? '');
  const [type, setType] = useState<AccountType>(editAccount?.type ?? 'BANK');
  const [currency, setCurrency] = useState(editAccount?.currency ?? homeCurrency);
  const [currencySearch, setCurrencySearch] = useState('');

  const filteredCurrencies = useMemo(() => {
    const q = currencySearch.toLowerCase();
    if (!q) return CURRENCIES;
    return CURRENCIES.filter(
      (c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    );
  }, [currencySearch]);
  const [parentId, setParentId] = useState(editAccount?.parentId ?? '');
  const [description, setDescription] = useState(editAccount?.description ?? '');
  const [code, setCode] = useState(editAccount?.code ?? '');
  const [hidden, setHidden] = useState(editAccount?.hidden ?? false);
  const [placeholder, setPlaceholder] = useState(editAccount?.placeholder ?? false);
  const [openingBalance, setOpeningBalance] = useState('');
  const [openingBalanceDate, setOpeningBalanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [ticker, setTicker] = useState(editAccount?.commodity?.ticker ?? '');
  const [exchange, setExchange] = useState(editAccount?.commodity?.exchange ?? '');
  const [fullName, setFullName] = useState(editAccount?.commodity?.fullName ?? '');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const possibleParents = accounts.filter((a) => !isEdit || a.id !== editAccount!.id);
  const showOpeningBalance = !isEdit && OPENING_BALANCE_TYPES.has(type) && !placeholder;
  const showCommodity = COMMODITY_TYPES.has(type);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError(t('accountForm.nameRequired'));
      return;
    }

    setSaving(true);
    try {
      const nextSeq = ledger.lastSequence + 1;

      if (isEdit) {
        const trimmedCode = code.trim();
        const commodity = showCommodity && ticker ? { ticker, exchange, fullName } : undefined;
        const payload: AccountUpdatedPayload = {
          accountId: editAccount!.id,
          name: name.trim(),
          description: description.trim(),
          ...(trimmedCode ? { code: trimmedCode } : {}),
          hidden,
          placeholder,
          parentId: parentId || null,
          ...(commodity !== undefined ? { commodity } : {}),
        };
        await encryptAndPush(await buildEvent('AccountUpdated', payload, nextSeq));
      } else {
        const obCents = openingBalance ? Math.round(parseFloat(openingBalance) * 100) : undefined;
        const trimmedCode = code.trim();
        const commodity = showCommodity && ticker ? { ticker, exchange, fullName } : undefined;
        const validObCents = obCents && obCents !== 0 ? obCents : undefined;
        const payload: AccountCreatedPayload = {
          accountId: uuidv4(),
          name: name.trim(),
          type,
          currency: currency.trim().toUpperCase() || 'USD',
          parentId: parentId || null,
          description: description.trim(),
          ...(trimmedCode ? { code: trimmedCode } : {}),
          hidden,
          placeholder,
          ...(commodity !== undefined ? { commodity } : {}),
          ...(validObCents !== undefined ? { openingBalance: validObCents } : {}),
          ...(validObCents !== undefined ? { openingBalanceDate: openingBalanceDate } : {}),
        };
        await encryptAndPush(await buildEvent('AccountCreated', payload, nextSeq));
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:saveAccount'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!editAccount || deleteConfirm !== editAccount.name) return;
    setSaving(true);
    try {
      const payload: AccountDeletedPayload = {
        accountId: editAccount.id,
        reparentChildrenToId: editAccount.parentId,
      };
      await encryptAndPush(await buildEvent('AccountDeleted', payload, ledger.lastSequence + 1));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:deleteAccount'));
    } finally {
      setSaving(false);
    }
  }

  const groupedTypes = ACCOUNT_TYPES.reduce<Record<string, { value: AccountType; labelKey: AccountTypeLabelKey }[]>>(
    (acc, item) => {
      const existing = acc[item.groupKey] ?? [];
      acc[item.groupKey] = [...existing, { value: item.value, labelKey: item.labelKey }];
      return acc;
    },
    {},
  );

  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-bg-surface border border-border max-w-md w-full p-6">
          <h2 className="text-lg font-semibold text-negative mb-2">{t('accountForm.deleteAccount')}</h2>
          <p className="text-sm text-text-muted mb-4">
            {t('accountForm.deleteConfirm', { name: editAccount?.name })}
          </p>
          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}
          <div className="mb-4">
            <label className={labelCls}>
              {t('accountForm.typeNameToConfirm')} <code className="bg-bg px-1 text-text font-mono">{editAccount?.name}</code>
            </label>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="w-full bg-bg border border-negative text-text text-sm px-3 py-2 focus:outline-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleDelete}
              disabled={saving || deleteConfirm !== editAccount?.name}
              className="flex-1 py-2 bg-negative text-white text-sm font-medium hover:opacity-80 disabled:opacity-40 transition-opacity"
            >
              {saving ? t('accountForm.deleting') : t('accountForm.deleteAccount')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-text">
              {isEdit ? t('accountForm.editAccount') : t('accountForm.newAccount')}
            </h2>
            <button onClick={onClose} className="text-text-muted hover:text-text text-xl leading-none">&times;</button>
          </div>

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className={labelCls}>{t('accountForm.nameLabel')} *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
                placeholder={t('accountForm.namePlaceholder')}
                autoFocus
              />
            </div>

            {!isEdit ? (
              <div>
                <label className={labelCls}>{t('accountForm.typeLabel')} *</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as AccountType)}
                  className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
                >
                  {Object.entries(groupedTypes).map(([groupKey, types]) => (
                    <optgroup key={groupKey} label={t(`accounting:accountTypeGroups.${groupKey as AccountGroupKey}`)}>
                      {types.map(({ value, labelKey }) => (
                        <option key={value} value={value}>{t(`accounting:accountTypes.${labelKey}`)}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            ) : (
              <div className="text-sm text-text-muted bg-bg px-3 py-2 border border-border">
                {t('accountForm.summary', { type: editAccount!.type, currency: editAccount!.currency })}
                <span className="text-xs block mt-0.5 text-text-subtle">{t('accountForm.typeCurrencyLocked')}</span>
              </div>
            )}

            {!isEdit && (
              <div>
                <label className={labelCls}>{t('accountForm.currencyLabel')}</label>
                <input
                  type="text"
                  value={currencySearch || currency}
                  onChange={(e) => { setCurrencySearch(e.target.value); }}
                  onFocus={(e) => { setCurrencySearch(''); e.target.select(); }}
                  className={`${inputCls} font-mono`}
                  placeholder={t('accountForm.currencyDefaultPlaceholder', { currency: homeCurrency })}
                />
                {currencySearch && (
                  <div className="border border-border-strong bg-bg-surface max-h-40 overflow-y-auto mt-0.5">
                    {filteredCurrencies.slice(0, 20).map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => { setCurrency(c.code); setCurrencySearch(''); }}
                        className="w-full text-left px-3 py-1.5 text-sm text-text-muted hover:bg-bg-elevated hover:text-text flex items-center gap-2"
                      >
                        <span className="font-mono font-medium text-text w-12">{c.code}</span>
                        <span>{c.name}</span>
                        <span className="ml-auto text-text-subtle">{c.symbol}</span>
                      </button>
                    ))}
                    {filteredCurrencies.length === 0 && (
                      <div className="px-3 py-2 text-sm text-text-subtle">{t('accountForm.noCurrenciesMatch')}</div>
                    )}
                  </div>
                )}
                {!currencySearch && currency !== homeCurrency && (
                  <p className="text-xs text-accent mt-1">{t('accountForm.foreignCurrencyNote')}</p>
                )}
              </div>
            )}

            {showCommodity && (
              <div className="space-y-3 p-3 bg-bg border border-border-strong">
                <p className="text-xs font-medium text-text-muted uppercase tracking-wide">{t('accountForm.securityDetails')}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-text-muted mb-1">{t('accountForm.tickerSymbol')}</label>
                    <input
                      type="text"
                      value={ticker}
                      onChange={(e) => setTicker(e.target.value.toUpperCase())}
                      className="w-full bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none font-mono"
                      placeholder={t('accountForm.tickerPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-1">{t('accountForm.exchangeLabel')}</label>
                    <input
                      type="text"
                      value={exchange}
                      onChange={(e) => setExchange(e.target.value)}
                      className="w-full bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                      placeholder={t('accountForm.exchangePlaceholder')}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">{t('accountForm.fullSecurityName')}</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                    placeholder={t('accountForm.fullNamePlaceholder')}
                  />
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>{t('accountForm.parentAccount')} <span className="text-text-subtle">{t('accountForm.optional')}</span></label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
              >
                <option value="">{t('accountForm.noneTopLevel')}</option>
                {possibleParents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>{t('accountForm.accountCode')} <span className="text-text-subtle">{t('accountForm.optional')}</span></label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={`${inputCls} font-mono`}
                placeholder={t('accountForm.codePlaceholder')}
              />
            </div>

            <div>
              <label className={labelCls}>{t('columns.description')} <span className="text-text-subtle">{t('accountForm.optional')}</span></label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputCls}
              />
            </div>

            {showOpeningBalance && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t('glossary.openingBalance')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    className={`${inputCls} font-mono`}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('accountForm.openingBalanceDate')}</label>
                  <input
                    type="date"
                    value={openingBalanceDate}
                    onChange={(e) => setOpeningBalanceDate(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={placeholder}
                  onChange={(e) => setPlaceholder(e.target.checked)}
                  className="border-border"
                />
                <span className="text-sm text-text-muted">{t('accountForm.placeholderOption')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hidden}
                  onChange={(e) => setHidden(e.target.checked)}
                  className="border-border"
                />
                <span className="text-sm text-text-muted">{t('accountForm.hiddenOption')}</span>
              </label>
            </div>

            <div className="flex gap-3 pt-1">
              {isEdit && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-2 border border-negative text-negative text-sm hover:bg-negative hover:text-white transition-colors"
                >
                  {t('common:actions.delete')}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
              >
                {saving ? t('common:actions.saving') : isEdit ? t('common:actions.saveChanges') : t('accountForm.createAccount')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
